import { useEffect, useState } from 'react';
import { Choice, OnboardingSlideProps } from 'models';
import { useStore } from 'providers';
import { StyledButton, StyledButtonGroup } from './FoodInterests.styles';

const sanitiseChoiceIds = (choices: Choice[]) =>
  choices.filter(({ selected }) => selected).map(({ choiceID }) => choiceID);

const FoodInterests = ({
  onboardingData: { buttonState },
  setOnboardingData,
}: OnboardingSlideProps) => {
  const { store } = useStore();
  const { surveyQuestions } = store;

  const {
    dietaryPreferences: { questionID, choices },
  } = surveyQuestions || {};

  const [options, setOptions] = useState<Choice[]>([]);
  const [choiceIDs, setChoiceIDs] = useState<number[]>([]);

  useEffect(() => {
    if (!choices) return;
    setOptions(choices.map((option) => ({ ...option, selected: false })));
  }, [choices]);

  useEffect(() => {
    setOnboardingData({
      type: 'setSurveyAnswers',
      data: { surveyAnswers: { answer1: { questionID, choiceIDs } } },
    });
  }, [setOnboardingData, questionID, choiceIDs]);

  useEffect(() => {
    const updatedButtonState = { ...buttonState, foodInterests: !choiceIDs.length };
    const hasChanges = JSON.stringify(updatedButtonState) !== JSON.stringify(buttonState);

    if (hasChanges) {
      setOnboardingData({
        type: 'setButtonState',
        data: { buttonState: updatedButtonState },
      });
    }
  }, [setOnboardingData, buttonState, choiceIDs]);

  const handleOnClick = (idx: number) => {
    const optionsCopy = [...options];
    const selectAllOption = optionsCopy[0];
    const currentOption = optionsCopy[idx];
    const currentChoices = optionsCopy.filter(({ selected }) => selected);
    const allSelected = optionsCopy.every(({ selected }) => selected);

    const isToggleAll =
      idx === 0 || (currentChoices.length === optionsCopy.length - 2 && !currentOption.selected);

    if (isToggleAll) {
      const toggleAll = optionsCopy.map((option) => ({ ...option, selected: !allSelected }));
      const selected = selectAllOption.selected ? [] : [selectAllOption.choiceID];

      setOptions(toggleAll);
      setChoiceIDs(selected);
      return;
    }

    if (allSelected && !isToggleAll) {
      selectAllOption.selected = false;
    }

    currentOption.selected = !currentOption.selected;

    setOptions(optionsCopy);
    setChoiceIDs(sanitiseChoiceIds(optionsCopy));
  };

  return (
    <StyledButtonGroup>
      {options.map((option: Choice, idx: number) => {
        const { choiceID, choiceLabel, selected } = option;

        return (
          <StyledButton
            key={choiceID}
            value={choiceID}
            aria-label="Food interest"
            selected={selected}
            onClick={() => handleOnClick(idx)}
          >
            {choiceLabel}
          </StyledButton>
        );
      })}
    </StyledButtonGroup>
  );
};

export default FoodInterests;
